import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Convertisseur Mesures Cuisson - Conversion Tasse Cuillère Gramme ML";
const DESCRIPTION =
  "Convertisseur Mesures Cuisson - Conversion Tasse Cuillère Gramme ML pour adapter vos recettes, portions et conversions en cuisine. Resultat rapide et pratique pour cuisiner avec plus de precision chaque jour.";
const KEYWORDS =
  "convertisseur mesures cuisson conversion tasse cuillère gramme ml, calculatrice convertisseur mesures cuisson conversion tasse cuillère gramme ml, calcul en ligne, cuisine, recette, conversion, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/cooking-measurement/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Convertisseur Mesures Cuisson",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
  },
];

// Volume units
const VOLUME_UNITS = {
  ml: { factor: 1, name: "ml (ml)" },
  tsp: { factor: 4.92892, name: "Petite Cuillère (tsp)" },
  tbsp: { factor: 14.7868, name: "Cuillère à Soupe (tbsp)" },
  fl_oz: { factor: 29.5735, name: "Once Liquide (fl oz)" },
  cup: { factor: 236.588, name: "Tasse Américaine (cup)" },
  liter: { factor: 1000, name: "Litre (L)" },
};

// Weight units
const WEIGHT_UNITS = {
  g: { factor: 1, name: "Gramme (g)" },
  kg: { factor: 1000, name: "Kilogramme (kg)" },
  oz: { factor: 28.3495, name: "Once (oz)" },
  lb: { factor: 453.592, name: "Livre (lb)" },
};

// Ingredients with density
const INGREDIENTS = {
  water: { name: "Eau", density: 1.0 },
  milk: { name: "Lait", density: 1.03 },
  flour_ap: { name: "Farine All-Purpose", density: 0.529 },
  sugar_white: { name: "Sucre Blanc", density: 0.845 },
  butter: { name: "Beurre", density: 0.911 },
  honey: { name: "Miel", density: 1.42 },
  oil: { name: "Huile Végétale", density: 0.917 },
};

const CONVERSION_TYPES = {
  volume_to_volume: "Volume à Volume",
  weight_to_weight: "Poids à Poids",
  volume_to_weight: "Volume à Poids",
  weight_to_volume: "Poids à Volume",
};

function CookingMeasurementCalculator() {
  const [conversionType, setConversionType] = useState("volume_to_volume");
  const [fromUnit, setFromUnit] = useState("cup");
  const [toUnit, setToUnit] = useState("ml");
  const [ingredient, setIngredient] = useState("flour_ap");
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateResult = () => {
    setError("");
    setResult(null);

    const val = parseFloat(inputValue);
    if (!inputValue || isNaN(val) || val <= 0) {
      if (inputValue) {
        setError("Veuillez entrer une valeur correcte et positive");
      }
      return;
    }

    try {
      let convertedValue = 0;
      let formulaText = "";
      let fromUnitName = "";
      let toUnitName = "";

      if (conversionType === "volume_to_volume") {
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} ml/unité ÷ ${toFactor} ml/unité = ${convertedValue.toFixed(4)} ${toUnitName}`;

      } else if (conversionType === "weight_to_weight") {
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} g/unité ÷ ${toFactor} g/unité = ${convertedValue.toFixed(4)} ${toUnitName}`;

      } else if (conversionType === "volume_to_weight") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        const inGrams = inMl * density;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inMl.toFixed(2)} ml × ${density} g/ml = ${inGrams.toFixed(2)} g = ${convertedValue.toFixed(4)} ${toUnitName}`;

      } else if (conversionType === "weight_to_volume") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        const inMl = inGrams / density;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inGrams.toFixed(2)} g ÷ ${density} g/ml = ${inMl.toFixed(2)} ml = ${convertedValue.toFixed(4)} ${toUnitName}`;
      }

      setResult({
        convertedValue,
        formulaText,
        fromUnitName,
        toUnitName,
        inputValue: val,
      });
    } catch (err) {
      setError("Erreur Calcul: " + err.message);
    }
  };

  useEffect(() => {
    if (inputValue) {
      calculateResult();
    } else {
      setResult(null);
      setError("");
    }
  }, [conversionType, fromUnit, toUnit, ingredient, inputValue]);

  // Reset units when conversion type changes
  useEffect(() => {
    if (conversionType === "volume_to_volume") {
      setFromUnit("cup");
      setToUnit("ml");
    } else if (conversionType === "weight_to_weight") {
      setFromUnit("g");
      setToUnit("oz");
    } else if (conversionType === "volume_to_weight") {
      setFromUnit("cup");
      setToUnit("g");
    } else if (conversionType === "weight_to_volume") {
      setFromUnit("g");
      setToUnit("ml");
    }
    setResult(null);
    setInputValue("");
    setError("");
  }, [conversionType]);

  const handleClear = () => {
    setInputValue("");
    setResult(null);
    setError("");
  };

  const isVolumeFrom = conversionType === "volume_to_volume" || conversionType === "volume_to_weight";
  const isVolumeTo = conversionType === "volume_to_volume" || conversionType === "weight_to_volume";
  const needsIngredient = conversionType === "volume_to_weight" || conversionType === "weight_to_volume";

  const fromUnitsMap = isVolumeFrom ? VOLUME_UNITS : WEIGHT_UNITS;
  const toUnitsMap = isVolumeTo ? VOLUME_UNITS : WEIGHT_UNITS;

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Convertisseur Mesures Cuisson</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Convertisseur Mesures Cuisson</strong> vous aide à convertir entre différentes unités de cuisine avec précision.
                Convertissez tasses en ml, grammes en onces, ou qualconque combinaison d'unités pour suivre vos recettes avec exactitude.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Sélectionner Type Conversion</h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label >Type Conversion</label>
                  <select
                    className="form-select"
                    value={conversionType}
                    onChange={(e) => setConversionType(e.target.value)}
                  >
                    {Object.entries(CONVERSION_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val}</option>
                    ))}
                  </select>
                </div>
              </div>

              {needsIngredient && (
                <div className="row mt-3">
                  <div className="col-sm-12">
                    <label >Sélectionner Ingrédient</label>
                    <select
                      className="form-select"
                      value={ingredient}
                      onChange={(e) => setIngredient(e.target.value)}
                    >
                      {Object.entries(INGREDIENTS).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>De Unité</label>
                  <select
                    className="form-select"
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                  >
                    {Object.entries(fromUnitsMap).map(([key, val]) => (
                      <option key={key} value={key}>{val.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>À Unité</label>
                  <select
                    className="form-select"
                    value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}
                  >
                    {Object.entries(toUnitsMap).map(([key, val]) => (
                      <option key={key} value={key}>{val.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12">
                  <label >Valeur à Convertir</label>
                  <input
                    type="number"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Entrez valeur"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>
                  Effacer
                </button>
              </div>

              {error && <div className="alert alert-warning">{error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Résultat Conversion :</strong>
                  </h3>
                  <div className="mb-3">
                    <div style={{ padding: "15px", backgroundColor: "#f0f0f0", borderRadius: "5px", textAlign: "center" }}>
                      <p style={{ fontSize: "14px", margin: "5px 0" }}>
                        {result.inputValue.toFixed(4)} {result.fromUnitName}
                      </p>
                      <p style={{ fontSize: "24px", fontWeight: "bold", color: "#1a5f3f", margin: "10px 0" }}>
                        = {result.convertedValue.toFixed(4)} {result.toUnitName}
                      </p>
                    </div>
                  </div>
                  <div className="alert alert-info" style={{ fontSize: "14px" }}>
                    <strong>Formule :</strong> {result.formulaText}
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Convertisseur de mesures en cuisine</h3>
              </div>
              <div className="info-section-body">
                <p>
                  Quand on cuisine avec des recettes venant de différents pays, on se retrouve vite bloqué par des unités
                  qu'on n'utilise pas au quotidien — les tasses américaines, les onces, les cuillères à soupe en ml...
                  Ce convertisseur de mesures cuisson vous évite les erreurs en traduisant automatiquement entre toutes
                  ces unités. Que vous adaptiez une recette américaine ou que vous doubliez les quantités d'un gâteau,
                  les conversions se font en temps réel dès que vous entrez une valeur.
                </p>
                <p>
                  Il gère aussi les conversions volume ↔ poids pour les ingrédients courants comme la farine, le sucre
                  ou le beurre, car 1 tasse de farine ne pèse pas la même chose qu'1 tasse d'eau — chaque ingrédient
                  a sa propre densité.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Tableau des conversions fréquentes</h3>
              </div>
              <div className="info-section-body">
                <ul>
                  <li>1 tasse américaine = 236,6 ml</li>
                  <li>1 cuillère à soupe = 15 ml</li>
                  <li>1 cuillère à café = 5 ml</li>
                  <li>1 once liquide = 29,6 ml</li>
                  <li>1 once (poids) = 28,35 g</li>
                  <li>1 livre = 453,6 g</li>
                  <li>1 tasse de farine ≈ 125 g</li>
                  <li>1 tasse de sucre blanc ≈ 200 g</li>
                  <li>1 tasse de beurre ≈ 227 g</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Pourquoi les volumes et les poids diffèrent-ils ?</h3>
              </div>
              <div className="info-section-body">
                <p>
                  Une tasse mesure un volume fixe (236,6 ml), mais le poids en grammes dépend de la densité de
                  l'ingrédient. La farine tassée pèse plus que la farine aérée — c'est pourquoi les boulangers
                  professionnels préfèrent peser leurs ingrédients plutôt que les mesurer en volume. Pour la pâtisserie
                  de précision, pesez toujours : c'est plus fiable que les tasses et les cuillères.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookingMeasurementCalculator;
